import type { ProductForm } from './bank-account-product.form';
import type { FormFieldsConfig } from '@/shared/ui/field-config';
import { i18n } from '@lingui/core';
import type { AnyScreenAction, Navigation } from '@/shared/screen';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { action, can, defineScreen } from '@/shared/screen';
import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { productFilters } from './bank-account-product.filters';
import { PRODUCT_TABS } from './bank-account-product.search';
export const PRODUCT_PATH = '/cadastro/produtos-conta';
export const productPath = (id: string) => `${PRODUCT_PATH}/${encodeURIComponent(id)}`;
const idInput = z.object({ id: z.string().min(1), name: z.string().optional() });
const saveInput = z.object({
  id: z.string().optional(),
  values: z.record(z.string(), z.unknown()),
});
const cloneInput = idInput.extend({ tenant: z.string().min(1) });
const assignInput = idInput.extend({
  userId: z.string().optional(),
  groupName: z.string().optional(),
  tenant: z.string().optional(),
});
const shareInput = idInput.extend({
  principalType: z.enum(['User', 'Group', 'Tenant']),
  principalId: z.string().min(1),
  permissionLevelType: z.enum(['View', 'Edit']),
});
const loadApi = () => import('./bank-account-product.api');
export const productCreate = action({
  id: 'bankAccountProduct.create',
  kind: 'draft',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco para cadastrar um produto de conta.`,
  permission: can('BankAccountProduct', 'Create'),
  run: ({ nav }) => nav.to(productPath('novo')),
});
export const productOpen = action({
  id: 'bankAccountProduct.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Editar`,
  description: msg`Abre o cadastro de um produto de conta pelo id, com o formulário e as abas.`,
  input: idInput,
  run: ({ nav, input }) => nav.to(productPath(input.id)),
});
export const productDelete = action({
  id: 'bankAccountProduct.delete',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui um produto de conta pelo id. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: can('BankAccountProduct', 'Delete'),
  input: idInput,
  preview: (input) => ({
    title: t`Excluir ${input.name ?? input.id}?`,
    detail: t`Tem certeza que deseja excluir este produto de conta?`,
    tone: 'destructive',
  }),
  run: async ({ input }) => (await loadApi()).deleteBankAccountProduct(input.id),
});
export const productFilter = filterAction('bankAccountProduct.filter', productFilters, {
  description: msg`Filtra a lista de produtos de conta: searchString (busca livre), name (nome do produto), orderBy (proprietário) e initialDate (data da ocorrência, AAAA-MM-DD). Filtro omitido é limpo.`,
});
export const productPage = listPageAction('bankAccountProduct.list.page');
export const productTab = action({
  id: 'bankAccountProduct.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba do produto aberto: informacoes, envolvidos ou documentos.`,
  input: z.object({ tab: z.enum(PRODUCT_TABS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
});
async function configuredFields(fieldConfig?: FormFieldsConfig) {
  return fieldConfig ?? (await import('@/shared/config')).getRuntimeConfig().FORM_FIELDS ?? {};
}
async function assertRecordChanges(
  fieldConfig: FormFieldsConfig | undefined,
  initial: Record<string, unknown>,
  patch: Record<string, unknown>,
) {
  const { assertConfiguredFieldChanges } = await import('./bank-account-product.field-access');
  assertConfiguredFieldChanges(i18n, await configuredFields(fieldConfig), initial, patch);
}
async function runProductClone(
  input: z.infer<typeof cloneInput>,
  nav: Navigation,
  fieldConfig?: FormFieldsConfig,
) {
  const { cloneSchema } = await import('./bank-account-product.record-rules');
  cloneSchema(i18n).parse({
    tenant: input.tenant,
    keepSameName: input.name === undefined,
    name: input.name ?? '',
  });
  const api = await loadApi();
  const { getCurrentTenant } = await import('@/shared/http/client');
  const record = await api.getBankAccountProduct(input.id, getCurrentTenant());
  await assertRecordChanges(
    fieldConfig,
    { tenant: record.tenant ?? '', name: record.name, keepSameName: true },
    {
      tenant: input.tenant,
      name: input.name ?? record.name,
      keepSameName: input.name === undefined || input.name === record.name,
    },
  );
  const result = await api.cloneBankAccountProduct(record, input.name ?? record.name, input.tenant);
  await nav.to(productPath(typeof result === 'string' ? result : result.id));
  return result;
}
export const productClone = action({
  id: 'bankAccountProduct.clone',
  kind: 'write',
  label: msg`Clonar produto`,
  description: msg`Copia o produto de conta para o correspondente escolhido (tenant), com o mesmo nome ou com um nome novo, e abre a cópia. Pede a confirmação da pessoa.`,
  permission: can('BankAccountProduct', 'Update'),
  input: cloneInput,
  preview: (input) => ({
    title: t`Clonar produto`,
    detail: t`Criar uma cópia no correspondente ${input.tenant}.`,
  }),
  run: ({ input, nav }) => runProductClone(input, nav),
});
async function runProductAssign(
  input: z.infer<typeof assignInput>,
  fieldConfig?: FormFieldsConfig,
) {
  const { assignSchema } = await import('./bank-account-product.record-rules');
  assignSchema(i18n).parse({
    tenant: input.tenant ?? '',
    userId: input.userId ?? null,
    groupName: input.groupName ?? null,
    userIdDisplay: null,
    groupNameDisplay: null,
  });
  await assertRecordChanges(
    fieldConfig,
    { tenant: '', userId: null, groupName: null },
    {
      tenant: input.tenant ?? '',
      userId: input.userId || null,
      groupName: input.groupName || null,
    },
  );
  return (await loadApi()).assignBankAccountProduct(input.id, input);
}
export const productAssign = action({
  id: 'bankAccountProduct.assign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Atribui o produto de conta a um operador (userId) ou a um grupo (groupName) de um correspondente (tenant). Pede a confirmação da pessoa.`,
  permission: can('BankAccountProduct', 'Update'),
  input: assignInput,
  preview: (input) => ({
    title: t`Atribuir`,
    detail: t`Atribuir o registro a ${input.userId || input.groupName || input.tenant || ''}.`,
  }),
  run: ({ input }) => runProductAssign(input),
});
async function runProductShare(input: z.infer<typeof shareInput>, fieldConfig?: FormFieldsConfig) {
  const { getCurrentTenant } = await import('@/shared/http/client');
  await assertRecordChanges(
    fieldConfig,
    { principalType: '', principalId: '', permissionLevelType: '' },
    {
      principalType: input.principalType,
      principalId: input.principalId,
      permissionLevelType: input.permissionLevelType,
    },
  );
  return (await loadApi()).shareBankAccountProduct(input.id, input, getCurrentTenant());
}
export const productShare = action({
  id: 'bankAccountProduct.share',
  kind: 'write',
  label: msg`Compartilhar registro`,
  description: msg`Compartilha o produto de conta com um usuário, grupo ou correspondente, com permissão de ver ou de editar. Pede a confirmação da pessoa.`,
  permission: can('BankAccountProduct', 'Update'),
  input: shareInput,
  preview: (input) => ({
    title: t`Compartilhar registro`,
    detail: t`Compartilhar com ${input.principalId} (${input.permissionLevelType}).`,
  }),
  run: ({ input }) => runProductShare(input),
});
async function runProductSave(
  input: z.infer<typeof saveInput>,
  fieldConfig?: FormFieldsConfig,
  formValues?: () => ProductForm,
) {
  const api = await loadApi();
  const form = await import('./bank-account-product.form');
  const { getCurrentTenant } = await import('@/shared/http/client');
  const initial = formValues
    ? formValues()
    : input.id
      ? form.productToForm(await api.getBankAccountProduct(input.id, getCurrentTenant()))
      : form.PRODUCT_EMPTY;
  const { assertProductFieldChanges } = await import('./bank-account-product.field-access');
  const configured = await configuredFields(fieldConfig);
  assertProductFieldChanges(i18n, configured, initial, input.values);
  const values = form.prepareProductSave(i18n, initial, input.values);
  return api.saveBankAccountProduct(input.id, form.productToWire(values), getCurrentTenant());
}
export const productSave = action({
  id: 'bankAccountProduct.save',
  kind: 'write',
  permission: can('BankAccountProduct', 'Update'),
  label: msg`Salvar`,
  description: msg`Salva o produto de conta com as mesmas validações do formulário: informações, envolvidos e documentos obrigatórios. Os parâmetros de assinatura só configuram o produto. Pede a confirmação da pessoa.`,
  input: saveInput,
  preview: (input) => ({
    title: t`Salvar produto`,
    detail: t`Salvar informações, envolvidos e documentos obrigatórios de ${typeof input.values['name'] === 'string' ? input.values['name'] : (input.id ?? '')}.`,
  }),
  run: ({ input }) => runProductSave(input),
});
export const productRefresh = action({
  id: 'bankAccountProduct.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados do produto de conta aberto, sem mudar nada.`,
  run: ({ nav }) => nav.search({}),
});
export const productHistory = action({
  id: 'bankAccountProduct.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o painel com o histórico de alterações do produto de conta.`,
  run: ({ nav }) => nav.search({ sheet: 'historico' }),
});
const access = { flag: { key: 'BANK_ACCOUNT_PRODUCT_MENU', notEquals: 0 } } as const;
export const bankAccountProductListScreen = defineScreen({
  id: 'bankAccountProduct.list',
  path: PRODUCT_PATH,
  title: msg`Produtos de Conta`,
  description: msg`Lista de produtos de conta: busca, filtros, paginação, abrir, excluir e criar.`,
  permission: can('BankAccountProduct', 'Read'),
  access,
  place: { icon: 'landmark', section: 'register' },
  actions: [productCreate, productOpen, productDelete, productFilter, productPage],
});
export const bankAccountProductDetailScreen = defineScreen({
  id: 'bankAccountProduct.detail',
  path: `${PRODUCT_PATH}/$productId`,
  title: msg`Produto de conta`,
  description: msg`Cadastro de um produto de conta (novo ou edição): abas de informações, envolvidos e documentos obrigatórios; salvar, atualizar, compartilhar, clonar, atribuir, excluir e histórico.`,
  permission: can('BankAccountProduct', 'Read'),
  access,
  place: {
    icon: 'file-text',
    parent: 'bankAccountProduct.list',
    record: msg`Produto de conta`,
    tabs: [
      { id: 'informacoes', label: msg`Informações` },
      { id: 'envolvidos', label: msg`Envolvidos` },
      { id: 'documentos', label: msg`Documentos obrigatórios` },
    ],
  },
  actions: [
    productSave,
    productRefresh,
    productShare,
    productClone,
    productAssign,
    productDelete,
    productHistory,
    productTab,
  ],
});

export function productDetailAgentActions({
  creating,
  editable,
  root,
  canRead,
  canUpdate,
  canDelete,
  id,
  fieldConfig,
  formValues,
}: {
  creating: boolean;
  editable: boolean;
  root: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  id: string;
  fieldConfig?: FormFieldsConfig;
  formValues?: () => ProductForm;
}): AnyScreenAction[] {
  if (!canRead) return [];
  const actions: AnyScreenAction[] = [productTab];
  if (editable && root)
    actions.push({
      ...productSave,
      permission: can('BankAccountProduct', creating ? 'Create' : 'Update'),
      run: (ctx) =>
        runProductSave(
          { ...saveInput.parse(ctx.input), id: creating ? undefined : id },
          fieldConfig,
          formValues,
        ),
    });
  if (!creating) {
    actions.push(productRefresh);
    if (editable && root)
      actions.push(
        {
          ...productShare,
          run: ({ input }) => runProductShare(shareInput.parse(input), fieldConfig),
        },
        {
          ...productClone,
          run: ({ input, nav }) => runProductClone(cloneInput.parse(input), nav, fieldConfig),
        },
      );
    if (root && canUpdate)
      actions.push({
        ...productAssign,
        run: ({ input }) => runProductAssign(assignInput.parse(input), fieldConfig),
      });
    if (root && canDelete) actions.push(productDelete);
    if (root) actions.push(productHistory);
  }
  return actions;
}
